/**
 * The patient's Title (Mr. / Mrs. / Ms. / Miss) joined to their name for
 * display: "Mr." + "Ramesh Kumar" → "Mr. Ramesh Kumar".
 *
 * The title is stored in its own column (patients.title), never inside
 * patients.name — search, duplicate checks, merges and SMS all read the bare
 * name. Use this wherever a name is shown as a person's identity (headers,
 * banners, the queue). Leave it out where the name is data being matched or
 * sent (search boxes, SMS, AI context).
 *
 * Mirrors backend/utils/patientName.js#withTitle — change both together.
 */
function titleKey(t: string): string {
  return t.trim().replace(/\.+$/, '').toLowerCase();
}

export function withTitle(name: string | null | undefined, title: string | null | undefined): string {
  const n = (name ?? '').trim();
  const t = (title ?? '').trim();
  if (!n || !t) return n;
  // Older records sometimes have the title typed into the name itself —
  // never print "Mr. Mr. Ramesh".
  const firstWord = n.split(/\s+/)[0] ?? '';
  if (titleKey(firstWord) === titleKey(t)) return n;
  return `${t} ${n}`;
}

/** Same, for a patient-shaped object. */
export function patientDisplayName(p: { name?: string | null; title?: string | null } | null | undefined): string {
  return withTitle(p?.name, p?.title);
}

// A new name part starts after a space, hyphen, apostrophe (straight or
// curly) or full stop — "mary-ann", "d'souza", "s.r. kumar".
const NAME_PART_BREAK = /[\s\-'’.]/;

/**
 * Capitalizes the first letter of each name part as typed:
 * "suresh kumar" → "Suresh Kumar", "d'souza" → "D'Souza".
 *
 * Only first letters change; the rest is left exactly as typed, so
 * "McDonald" and "RAVI" stay as they are. A letter whose capital form has a
 * different length (e.g. "ß") is left alone, so the string never changes
 * length — that is what keeps the cursor in place while typing. Scripts with
 * no capitals (Tamil, Hindi) pass through unchanged.
 */
export function capitalizeNameParts(value: string): string {
  let out = '';
  let partStart = true;
  for (const ch of value) {
    if (partStart) {
      const upper = ch.toUpperCase();
      out += upper.length === ch.length ? upper : ch;
    } else {
      out += ch;
    }
    partStart = NAME_PART_BREAK.test(ch);
  }
  return out;
}

/**
 * For a name box's onChange: capitalizes the input in place and puts the
 * cursor back where it was, then returns the value to store in state.
 *
 * Writing the value to the DOM before React re-renders means React finds the
 * box already holding the new value and leaves the cursor alone — otherwise a
 * changed controlled value jumps the cursor to the end, so correcting the
 * middle of a name would be impossible. Skipped while an IME is composing
 * (mobile keyboards, Tamil input): rewriting the text mid-composition
 * duplicates characters. Call it again from onCompositionEnd.
 */
export function capitalizeNameInput(el: HTMLInputElement, composing = false): string {
  const { value } = el;
  if (composing) return value;
  const next = capitalizeNameParts(value);
  if (next !== value) {
    const { selectionStart, selectionEnd } = el;
    el.value = next;
    if (selectionStart != null && selectionEnd != null) el.setSelectionRange(selectionStart, selectionEnd);
  }
  return next;
}

/**
 * onChange + onCompositionEnd props for a patient-name <input>:
 *   <Input value={name} {...nameInputProps((v) => setName(v))} />
 */
export function nameInputProps(onValue: (value: string) => void) {
  return {
    autoCapitalize: 'words' as const,
    onChange: (e: { target: HTMLInputElement; nativeEvent: Event }) =>
      onValue(capitalizeNameInput(e.target, (e.nativeEvent as InputEvent).isComposing === true)),
    onCompositionEnd: (e: { currentTarget: HTMLInputElement }) => onValue(capitalizeNameInput(e.currentTarget)),
  };
}
